/**
 * Isolated render of a CMS entry in preview, `/{locale}/__preview/entry/{entryId}?root={ancestorId}`: the page
 * "Open in Preview" falls back to when no page shows the entry. Read by the SSR middleware (no routeInfo for it)
 * and by `APIRouter` (renders `PreviewEntryPage`), built by the preview locations resolver.
 * No path aliases here: also imported by the Express server.
 */

/**
 * Content id of the query of `PreviewEntryPage`: an entry by id, whatever its content type (the query builder
 * sends no `content_type` for it).
 */
export const PREVIEW_ENTRY_CONTENT_ID = 'previewEntry';

/** Entry ids accepted in the path and in `root`. */
export const PREVIEW_ENTRY_ID_REGEX = /^[\w.-]{1,64}$/;

const PREVIEW_ENTRY_PATH_REGEX = /^\/__preview\/entry\/([\w.-]{1,64})\/?$/;

/**
 * Path of the isolated render of an entry, without the locale prefix.
 * @param {string} entryId - Entry to show
 * @param {string} [root] - Ancestor rendered in its place, when the entry is not rendered on its own
 * @returns {string}
 */
export const buildPreviewEntryPath = (entryId: string, root?: string): string =>
	`/__preview/entry/${entryId}${root && root !== entryId ? `?root=${encodeURIComponent(root)}` : ''}`;

/**
 * Entry id of an isolated render path.
 * @param {string | undefined} path - Path without the locale prefix (query string allowed)
 * @returns {string | null} The entry id, or `null` when the path is another one
 */
export const parsePreviewEntryPath = (path: string | undefined): string | null =>
	path?.split('?')[0].match(PREVIEW_ENTRY_PATH_REGEX)?.[1] ?? null;
